import {ISSUE_VIEWER_CACHE_TTL_MS} from '@github-ui/preheat-issues-manager/issue-viewer-storage-key'
import {type InfiniteData, infiniteQueryOptions} from '@github-ui/react-query'
import type {createServerPersistedQueryPersister} from '@github-ui/react-query-persister'
import {
  IssueRelationshipsBlockedBy,
  IssueRelationshipsBlocking,
  IssueRelationshipsRelatesTo,
} from '@github-ui/server-persisted-queries'
import type {IssueRelationshipsBlockedByQuery} from '@github-ui/server-persisted-queries/types/IssueRelationshipsBlockedBy'
import type {IssueRelationshipsBlockingQuery} from '@github-ui/server-persisted-queries/types/IssueRelationshipsBlocking'
import type {IssueRelationshipsRelatesToQuery} from '@github-ui/server-persisted-queries/types/IssueRelationshipsRelatesTo'
import {reactFetchJSON} from '@github-ui/verified-fetch'

export const RELATIONSHIPS_PAGE_SIZE = 25

/**
 * The persister is passed in rather than built here: these are plain option factories with no hook
 * context, and `createServerPersistedQueryPersister` needs `sendAnalyticsEvent` from the component.
 */
type RelationshipsPersister = ReturnType<typeof createServerPersistedQueryPersister>

type BlockingIssue = NonNullable<NonNullable<IssueRelationshipsBlockingQuery['repository']>['issue']>
type BlockedByIssue = NonNullable<NonNullable<IssueRelationshipsBlockedByQuery['repository']>['issue']>
type RelatesToIssue = NonNullable<NonNullable<IssueRelationshipsRelatesToQuery['repository']>['issue']>
type BlockingConnection = BlockingIssue['blocking']
type BlockedByConnection = BlockedByIssue['blockedBy']
type RelatesToConnection = RelatesToIssue['relatesTo']
type RelationshipsConnection = BlockingConnection | BlockedByConnection | RelatesToConnection
type BlockingNode = NonNullable<NonNullable<BlockingConnection['edges']>[number]>['node']
type BlockedByNode = NonNullable<NonNullable<BlockedByConnection['edges']>[number]>['node']
type RelatesToNode = NonNullable<NonNullable<RelatesToConnection['edges']>[number]>['node']
type RelationshipNode = NonNullable<BlockingNode | BlockedByNode | RelatesToNode>
type RelationshipsQuery =
  | IssueRelationshipsBlockingQuery
  | IssueRelationshipsBlockedByQuery
  | IssueRelationshipsRelatesToQuery

type RelationshipsResponse<TQuery extends RelationshipsQuery> = {
  data?: TQuery
  errors?: unknown[]
}

export type RelationshipsPage = {
  nodes: RelationshipNode[]
  repositoryNameWithOwner: string
  pageInfo: {hasNextPage: boolean; endCursor: string | null}
}

export function blockingRelationshipsQueryKey(owner: string, repo: string, number: number) {
  return ['issue-viewer', 'relationships', 'blocking', {owner, repo, number}] as const
}

export function blockedByRelationshipsQueryKey(owner: string, repo: string, number: number) {
  return ['issue-viewer', 'relationships', 'blocked-by', {owner, repo, number}] as const
}

export function relatesToRelationshipsQueryKey(owner: string, repo: string, number: number) {
  return ['issue-viewer', 'relationships', 'relates-to', {owner, repo, number}] as const
}

async function fetchRelationshipsPage<TQuery extends RelationshipsQuery>(
  url: string,
  queryId: string,
  cursor: string | null,
  selectIssue: (data: TQuery) => {
    connection: RelationshipsConnection
    repositoryNameWithOwner: string
  } | null,
  signal?: AbortSignal,
): Promise<RelationshipsPage> {
  const params = new URLSearchParams({page_size: String(RELATIONSHIPS_PAGE_SIZE), queryId})
  if (cursor) params.set('cursor', cursor)

  const response = await reactFetchJSON(`${url}?${params.toString()}`, {signal})
  if (!response.ok) {
    throw new Error(`Relationships fetch failed (${response.status})`)
  }

  const json = (await response.json()) as RelationshipsResponse<TQuery>
  if (!json.data) {
    throw new Error('Relationships query returned no data')
  }

  const issue = selectIssue(json.data)
  const nodes = issue?.connection.edges?.flatMap(edge => (edge?.node ? [edge.node] : [])) ?? []

  return {
    nodes,
    repositoryNameWithOwner: issue?.repositoryNameWithOwner ?? '',
    pageInfo: issue?.connection.pageInfo ?? {hasNextPage: false, endCursor: null},
  }
}

export function blockingInfiniteQueryOptions(
  owner: string,
  repo: string,
  number: number,
  persister?: RelationshipsPersister,
) {
  return infiniteQueryOptions<
    RelationshipsPage,
    Error,
    InfiniteData<RelationshipsPage>,
    ReturnType<typeof blockingRelationshipsQueryKey>,
    string | null
  >({
    queryKey: blockingRelationshipsQueryKey(owner, repo, number),
    initialPageParam: null as string | null,
    queryFn: ({pageParam, signal}) =>
      fetchRelationshipsPage<IssueRelationshipsBlockingQuery>(
        `/${owner}/${repo}/issues/${number}/relationships/blocking.json`,
        IssueRelationshipsBlocking,
        pageParam,
        data => {
          const issue = data.repository?.issue
          return issue ? {connection: issue.blocking, repositoryNameWithOwner: issue.repository.nameWithOwner} : null
        },
        signal,
      ),
    getNextPageParam: (lastPage: RelationshipsPage) =>
      lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined,
    gcTime: ISSUE_VIEWER_CACHE_TTL_MS,
    persister,
  })
}

export function blockedByInfiniteQueryOptions(
  owner: string,
  repo: string,
  number: number,
  persister?: RelationshipsPersister,
) {
  return infiniteQueryOptions<
    RelationshipsPage,
    Error,
    InfiniteData<RelationshipsPage>,
    ReturnType<typeof blockedByRelationshipsQueryKey>,
    string | null
  >({
    queryKey: blockedByRelationshipsQueryKey(owner, repo, number),
    initialPageParam: null as string | null,
    queryFn: ({pageParam, signal}) =>
      fetchRelationshipsPage<IssueRelationshipsBlockedByQuery>(
        `/${owner}/${repo}/issues/${number}/relationships/blocked_by.json`,
        IssueRelationshipsBlockedBy,
        pageParam,
        data => {
          const issue = data.repository?.issue
          return issue ? {connection: issue.blockedBy, repositoryNameWithOwner: issue.repository.nameWithOwner} : null
        },
        signal,
      ),
    getNextPageParam: (lastPage: RelationshipsPage) =>
      lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined,
    gcTime: ISSUE_VIEWER_CACHE_TTL_MS,
    persister,
  })
}

export function relatesToInfiniteQueryOptions(
  owner: string,
  repo: string,
  number: number,
  persister?: RelationshipsPersister,
) {
  return infiniteQueryOptions<
    RelationshipsPage,
    Error,
    InfiniteData<RelationshipsPage>,
    ReturnType<typeof relatesToRelationshipsQueryKey>,
    string | null
  >({
    queryKey: relatesToRelationshipsQueryKey(owner, repo, number),
    initialPageParam: null as string | null,
    queryFn: ({pageParam, signal}) =>
      fetchRelationshipsPage<IssueRelationshipsRelatesToQuery>(
        `/${owner}/${repo}/issues/${number}/relationships/relates_to.json`,
        IssueRelationshipsRelatesTo,
        pageParam,
        data => {
          const issue = data.repository?.issue
          return issue ? {connection: issue.relatesTo, repositoryNameWithOwner: issue.repository.nameWithOwner} : null
        },
        signal,
      ),
    getNextPageParam: (lastPage: RelationshipsPage) =>
      lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined,
    gcTime: ISSUE_VIEWER_CACHE_TTL_MS,
    persister,
  })
}
